import toast from 'react-hot-toast';
import { getToken, setToken, clearToken, stashAdminToken, popAdminToken } from '../utils/tokenStore';
import { apiFetch } from '../utils/apiFetch';

export interface User {
  id: string;
  email: string;
  first_name?: string;
  last_name?: string;
  role?: string;
  avatar_url?: string;
  tenant_id?: number;
}

class AuthService {
  private baseURL = '/api/auth';

  /**
   * Login: POST credentials, receive { token, user }, store token, return user.
   */
  async login(email: string, password: string): Promise<{ token: string; user: User } | null> {
    const response = await fetch(`${this.baseURL}/login`, {
      method: 'POST',
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ email, password }),
    });

    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      const msg = err.message || err.error || 'Invalid credentials';
      throw new Error(msg);
    }

    const data = await response.json();
    if (data.token) {
      setToken(data.token);
    }
    return data;
  }

  async getCurrentUser(): Promise<any> {
    try {
      if (!getToken()) return null;

      const response = await apiFetch(`${this.baseURL}/me`);
      if (!response.ok) {
        if (response.status === 401) {
          clearToken();
          return null;
        }
        throw new Error('Failed to get user info');
      }

      const data = await response.json();
      return data.authenticated ? data : null;
    } catch (error) {
      console.error('Get current user error:', error);
      return null;
    }
  }

  async impersonate(userId: string): Promise<any> {
    try {
      const adminToken = getToken();
      if (!adminToken) {
        throw new Error('Not authenticated');
      }

      const response = await apiFetch(`${this.baseURL}/impersonate/${userId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.error || 'Failed to impersonate user');
      }

      const data = await response.json();

      // Stash admin token so stop-impersonating can restore it.
      stashAdminToken(adminToken);
      if (data.token) {
        setToken(data.token);
      }

      toast.success(data.message || 'Now impersonating user');
      return data;
    } catch (error: any) {
      console.error('Impersonate error:', error);
      toast.error(error.message || 'Failed to impersonate user');
      throw error;
    }
  }

  async stopImpersonating(): Promise<any> {
    try {
      // Revoke the impersonation token on the server.
      const response = await apiFetch(`${this.baseURL}/stop-impersonating`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.error || 'Failed to stop impersonating');
      }

      const data = await response.json();

      // Restore the original admin token (stashed during impersonate()).
      const adminToken = popAdminToken();
      if (adminToken) {
        setToken(adminToken);
      } else {
        clearToken();
      }

      toast.success(data.message || 'Returned to admin account');
      return data;
    } catch (error: any) {
      console.error('Stop impersonate error:', error);
      toast.error(error.message || 'Failed to stop impersonating');
      throw error;
    }
  }

  async checkAuth(): Promise<boolean> {
    if (!getToken()) return false;
    try {
      const response = await apiFetch(`${this.baseURL}/check`);
      if (!response.ok) {
        if (response.status === 401) clearToken();
        return false;
      }
      const data = await response.json();
      return data.authenticated === true;
    } catch (error) {
      console.error('Check auth error:', error);
      return false;
    }
  }

  async logout(): Promise<void> {
    try {
      // Revoke the token server-side. Best-effort — clear locally regardless.
      if (getToken()) {
        await apiFetch(`${this.baseURL}/logout`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
        });
      }
      toast.success('Logged out successfully');
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      clearToken();
      // Drop any stashed admin token too — a fresh sign-in is needed.
      popAdminToken();
    }
  }
}

export const authService = new AuthService();
